/* eslint-disable no-useless-escape */
import AWS from 'aws-sdk'
import axios from 'axios'
import imageCompression from 'browser-image-compression'
import { AES, enc, mode, pad } from 'crypto-js'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
// import { urlRegExp } from './constants'
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'
import logo from '../resources/images/watermark.png'
import font from '../resources/fonts/font.ttf'
import {
  TextractClient,
  DetectDocumentTextCommand,
  AnalyzeDocumentCommand,
} from '@aws-sdk/client-textract'
import { Buffer } from 'buffer'
import mammoth from 'mammoth'

export const formatCount = (count) => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`
  }
  return count.toString()
}

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer
}

const IS_TEXTRACT_ALLOWED_KEY = 'isTextractAllowed'

export const setIsTextractAllowed = (value) => {
  const allowed = value === true || value === 'true'
  localStorage.setItem(IS_TEXTRACT_ALLOWED_KEY, String(allowed))
}

export const getIsTextractAllowed = () =>
  localStorage.getItem(IS_TEXTRACT_ALLOWED_KEY) === 'true'

const emptyTextractResult = () => ({
  detectedText: '',
  phoneNumbers: [],
  emails: [],
  numbersOnly: [],
  containsAtSymbol: false,
  skipped: true,
})

export const processDocumentWithTextract = async (file) => {
  if (!getIsTextractAllowed()) {
    return emptyTextractResult()
  }

  try {
    const fileType = file.type.split('/')[0]
    const fileExtension = file.name.split('.').pop().toLowerCase()
    const allowedDocuments = ['txt', 'pdf', 'docx']
    const MAX_FILE_SIZE = 10 * 1024 * 1024

    // Compress or process file based on type and size
    let processedFile = file
    if (fileType === 'image' && file.size > MAX_FILE_SIZE) {
      const compressedFile = await CompressImage(file)
      if (compressedFile) {
        processedFile = compressedFile
      } else {
        throw new Error('File compression failed')
      }
    }

    let base64String
    if (fileType === 'image') {
      base64String = await fileToBase64(processedFile)
    } else if (allowedDocuments.includes(fileExtension)) {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('Document size exceeds 10MB limit')
      }
      base64String = await documentToBase64(processedFile)
    } else {
      throw new Error('Unsupported file type')
    }

    let textractResponse
    if (['pdf'].includes(fileExtension)) {
      // Use AnalyzeDocument for PDF files
      textractResponse = await sendToTextract(base64String, fileExtension, true)
    } else if (fileExtension === 'docx') {
      // For DOCX, use mammoth to extract text
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      return {
        detectedText: result.value,
        phoneNumbers: extractPhoneNumbers(result.value),
        emails: extractEmails(result.value),
        numbersOnly: extractNumbersOnly(result.value),
        containsAtSymbol: result.value.includes('@'),
      }
    } else {
      textractResponse = await sendToTextract(base64String, fileExtension)
    }
    return extractInformation(textractResponse)
  } catch (error) {
    console.error('Error processing file:', error)
    if (error.message.includes('Textract')) {
      console.error('Textract error details:', error.details)
    }
    throw error
  }
}

const getS3Token = async () => {
  const config = {
    withCredentials: true,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  }

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/aws/token`,
      config,
    )

    if (response?.data) {
      return {
        IdentityId: response?.data?.data?.IdentityId,
        Token: response?.data?.data?.Token,
      }
    }
  } catch (error) {
    console.error(error)
    if (error?.response?.data?.statusCode === 401) {
      if (error?.response?.data?.message) {
        enqueueSnackbar(error?.response?.data?.message, {
          variant: 'error',
        })
      }
    }
    return
  }
}

const sendToTextract = async (
  base64String,
  fileExtension,
  useAnalyzeDocument = false,
) => {
  const region = process.env.REACT_APP_AWS_REGION
  const identityId = process.env.REACT_APP_IDENTITY_POOL_ID
  const tempAwsToken = await getS3Token()

  if (!region || !identityId || !tempAwsToken) {
    throw new Error(
      'AWS credentials are missing. Please check your environment variables.',
    )
  }

  try {
    AWS.config.update({ region })

    const credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
      IdentityId: tempAwsToken?.IdentityId,
      Logins: { 'cognito-identity.amazonaws.com': tempAwsToken?.Token },
    })
    await credentials.getPromise()

    const textractClient = new TextractClient({ region, credentials })

    const documentBytes = Buffer.from(base64String, 'base64')

    const params = { Document: { Bytes: documentBytes } }

    if (useAnalyzeDocument) {
      const command = new AnalyzeDocumentCommand({
        ...params,
        FeatureTypes: ['TABLES', 'FORMS'],
      })

      return await textractClient.send(command)
    } else {
      const command = new DetectDocumentTextCommand(params)
      return await textractClient.send(command)
    }
  } catch (error) {
    console.error('Textract error:', error)
    error.message = `Textract error: ${error.message}`
    throw error
  }
}

const extractInformation = (data) => {
  let detectedText = ''
  if (data.Blocks) {
    detectedText = data.Blocks.filter((block) => block.BlockType === 'LINE')
      .map((line) => line.Text)
      .join('\n')
  } else if (data.Pages) {
    data.Pages.forEach((page) => {
      page.Lines.forEach((line) => {
        detectedText += line.Text + '\n'
      })
    })
  }

  return {
    detectedText,
    phoneNumbers: extractPhoneNumbers(detectedText),
    emails: extractEmails(detectedText),
    numbersOnly: extractNumbersOnly(detectedText),
    containsAtSymbol: detectedText.includes('@'),
  }
}

const extractPhoneNumbers = (text) => {
  const phoneRegex = /\+?\d{1,3}?\s?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}/g
  return text.match(phoneRegex) || []
}

const extractEmails = (text) => {
  const emailRegex =
    /[a-zA-Z0-9._%+-]+\s?@\s?[a-zA-Z0-9.-]+\s?\.\s?[a-zA-Z]{2,6}/g
  return text.match(emailRegex) || []
}

const extractNumbersOnly = (text) => {
  const numbersOnlyRegex = /\b\d{5}(?:[-.\s]?\d{5})?\b/g
  return text.match(numbersOnlyRegex) || []
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result
        .replace('data:', '')
        .replace(/^.+,/, '')
      resolve(base64String)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

const documentToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const arrayBuffer = reader.result
      const base64String = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          '',
        ),
      )
      resolve(base64String)
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export const ProcessVideo = async (videoBlob, senderName) => {
  const ffmpeg = new FFmpeg()
  await ffmpeg.load()

  // Determine correct input extension
  const getExtensionFromMime = (mimeType) => {
    const mimeMap = {
      'video/mp4': 'mp4',
      'video/quicktime': 'mov',
      'video/webm': 'webm',
      'video/ogg': 'ogv',
      'video/x-quicktime': 'mov',
    }
    return mimeMap[mimeType] || 'mp4' // default to mp4 if unknown
  }

  const inputExtension = getExtensionFromMime(videoBlob.type)
  const inputFileName = `input.${inputExtension}`
  const outputFileName = 'output.mp4'

  await ffmpeg.writeFile(inputFileName, await fetchFile(videoBlob))

  const logoResponse = await fetch(logo)
  const fontResponse = await fetch(font)
  const logoBlob = await logoResponse.blob()
  const fontBlob = await fontResponse.blob()
  await ffmpeg.writeFile('logo.png', await fetchFile(logoBlob))
  await ffmpeg.writeFile('font.ttf', await fetchFile(fontBlob))

  await ffmpeg.exec([
    '-i',
    inputFileName,
    '-i',
    'logo.png',
    '-filter_complex',
    "[0]scale='if(gt(iw,ih),1920,-1)':'if(gt(iw,ih),-1,1920)'[vid];" +
      '[1]scale=iw*0.10:-2,colorchannelmixer=aa=0.5[logo];' +
      '[vid][logo]overlay=x=trunc((((main_w-overlay_w)/2)/2)*2):y=trunc((((main_h-overlay_h)/2)/2)*2)[ovid];' +
      `[ovid]drawtext=text='\@${senderName}':fontfile=font.ttf:fontcolor=white@0.3:` +
      'x=trunc((((main_w-text_w)/2)/2)*2):y=trunc((((main_h-text_h)/2)/2)*2)+50:fontsize=32[fvid]',
    '-map',
    '[fvid]',
    '-map',
    '0:a?',
    '-c:v',
    'libx264',
    '-preset',
    'ultrafast',
    '-crf',
    '28',
    '-maxrate',
    '2M',
    '-bufsize',
    '4M',
    '-c:a',
    'aac',
    '-b:a',
    '96k',
    '-ac',
    '2',
    '-r',
    '24',
    '-movflags',
    '+faststart',
    '-sn',
    '-threads',
    '0',
    outputFileName,
  ])

  const data = await ffmpeg.readFile(outputFileName)
  const processedBlob = new Blob([data.buffer], { type: 'video/mp4' })
  return processedBlob
}

export const ProcessImage = async (
  imageBlob,
  senderName = '',
  addWatermark = true,
) => {
  // Function to process image with FFmpeg
  const processWithFFmpeg = async (inputBlob, senderName, addWatermark) => {
    const ffmpeg = new FFmpeg()
    await ffmpeg.load()

    const fileType = inputBlob.type.split('/')[1]
    const inputFileName = `input.${fileType}`
    const outputFileName = `output.${fileType}`

    await ffmpeg.writeFile(inputFileName, await fetchFile(inputBlob))
    const fontResponse = await fetch(font)
    const fontBlob = await fontResponse.blob()
    await ffmpeg.writeFile('font.ttf', await fetchFile(fontBlob))

    let filterComplex = ''
    if (addWatermark) {
      const logoResponse = await fetch(logo)
      const logoBlob = await logoResponse.blob()
      await ffmpeg.writeFile('logo.png', await fetchFile(logoBlob))
      if (senderName) {
        filterComplex =
          "[0]scale='if(gt(iw,ih),1920,-1)':'if(gt(iw,ih),-1,1920)'[vid];" +
          '[1]scale=iw*0.10:-2,colorchannelmixer=aa=0.5[logo];' +
          '[vid][logo]overlay=x=trunc((((main_w-overlay_w)/2)/2)*2):y=trunc((((main_h-overlay_h)/2)/2)*2)[ovid];' +
          `[ovid]drawtext=fontfile=font.ttf:text='\@${senderName}':` +
          'fontcolor=white@0.3:fontsize=32:' +
          'x=trunc((((main_w-text_w)/2)/2)*2):y=trunc((((main_h-text_h)/2)/2)*2)+50[fvid]'
      } else {
        filterComplex =
          "[0]scale='if(gt(iw,ih),1920,-1)':'if(gt(iw,ih),-1,1920)'[vid];" +
          '[1]scale=iw*0.10:-2,colorchannelmixer=aa=0.5[logo];' +
          '[vid][logo]overlay=x=trunc((((main_w-overlay_w)/2)/2)*2):y=trunc((((main_h-overlay_h)/2)/2)*2)[ovid];' +
          '[ovid]copy[fvid]'
      }
    } else {
      filterComplex =
        "[0]scale='if(gt(iw,ih),1920,-1)':'if(gt(iw,ih),-1,1920)'[vid];" +
        `[vid]drawtext=fontfile=font.ttf:text='\@${senderName}':` +
        'fontcolor=white@0.3:fontsize=32:' +
        'x=trunc((((main_w-text_w)/2)/2)*2):y=trunc((((main_h-text_h)/2)/2)*2)[fvid]'
    }

    const ffmpegArgs = ['-i', inputFileName]
    if (addWatermark) {
      ffmpegArgs.push('-i', 'logo.png')
    }

    ffmpegArgs.push(
      '-filter_complex',
      filterComplex,
      '-map',
      '[fvid]',
      '-q:v',
      '20',
      '-f',
      'image2',
      outputFileName,
    )

    await ffmpeg.exec(ffmpegArgs)
    const data = await ffmpeg.readFile(outputFileName)
    return new Blob([data.buffer], { type: `image/${fileType}` })
  }

  try {
    let processedBlob
    let attempts = 0
    const maxAttempts = 3

    while (attempts < maxAttempts) {
      try {
        processedBlob = await processWithFFmpeg(
          imageBlob,
          senderName,
          addWatermark,
        )

        break // If successful, exit the loop
      } catch (error) {
        console.warn(`Attempt ${attempts + 1} failed:`, error)
        attempts++
        if (attempts >= maxAttempts) {
          throw error // Rethrow the error if all attempts fail
        }
      }
    }

    if (!processedBlob) {
      throw new Error('Failed to process image after multiple attempts')
    }

    const outputFile = new File([processedBlob], 'output.jpg', {
      type: processedBlob.type,
    })

    const compressedFile = await CompressImage(outputFile)
    return compressedFile
  } catch (error) {
    console.error('FFmpeg processing error:', error)
    return null
  }
}

export const retry = (fn, retriesLeft = 5, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // reject('maximum retries exceeded');
            reject(error)
            return
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject)
        }, interval)
      })
  })
}

export const isNumber = (number) => {
  const isNum = /^\d+$/.test(number)

  return isNum
}

export const shortName = (name) => {
  return name
    ? name
        ?.match(/\b(\w)/g)
        ?.slice(0, 2)
        ?.join('')
        ?.toUpperCase()
    : ''
}

export const formatNumber = (number) => {
  if (number < 0) return 0

  if (number >= 10000000)
    number =
      (number / 10000000).toLocaleString('en-IN', {
        maximumFractionDigits: 2,
      }) + ' Cr'
  else if (number >= 100000)
    number =
      (number / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 }) +
      ' Lac'
  else if (number >= 1000)
    number = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return number

  // return number > 0
  //   ? number.toLocaleString('en-IN', { maximumFractionDigits: 2 })
  //   : 0
}

export const formatNumberWithPrecision = (number) => {
  return number > 0 ? Math.round((number + Number.EPSILON) * 100) / 100 : 0
}

export const formatNumberWithNoPrecision = (number) => {
  return number > 0 ? number.toFixed(0) : 0
}

export const getPercentage = (number1, number2) => {
  const offPercentage = number2
    ? number2 !== '0'
      ? formatNumberWithPrecision(((number1 - number2) / number1) * 100)
      : 0
    : 0

  return offPercentage + '% Off'
}

export const isValidPostalCode = (postalCode, countryCode) => {
  let postalCodeRegex = /^(?:[A-Z0-9]+([- ]?[A-Z0-9]+)*)?$/

  switch (countryCode) {
    case 'GB':
      postalCodeRegex =
        /^GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/
      break
    case 'JE':
      postalCodeRegex = /^JE\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/
      break
    case 'GG':
      postalCodeRegex = /^GY\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/
      break
    case 'IM':
      postalCodeRegex = /^IM\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/
      break
    case 'US':
      postalCodeRegex = /^\d{5}([ \-]\d{4})?$/
      break
    case 'CA':
      postalCodeRegex =
        /[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/
      break
    case 'DE':
      postalCodeRegex = /^\d{5}$/
      break
    case 'JP':
      postalCodeRegex = /^\d{3}-\d{4}$/
      break
    case 'FR':
      postalCodeRegex = /^\d{2}[ ]?\d{3}$/
      break
    case 'AU':
      postalCodeRegex = /^\d{4}$/
      break
    case 'IT':
      postalCodeRegex = /^\d{5}$/
      break
    case 'CH':
      postalCodeRegex = /^\d{4}$/
      break
    case 'AT':
      postalCodeRegex = /^\d{4}$/
      break
    case 'ES':
      postalCodeRegex = /^\d{5}$/
      break
    case 'NL':
      postalCodeRegex = /^\d{4}[ ]?[A-Z]{2}$/
      break
    case 'BE':
      postalCodeRegex = /^\d{4}$/
      break
    case 'DK':
      postalCodeRegex = /^\d{4}$/
      break
    case 'SE':
      postalCodeRegex = /^\d{3}[ ]?\d{2}$/
      break
    case 'NO':
      postalCodeRegex = /^\d{4}$/
      break
    case 'BR':
      postalCodeRegex = /^\d{5}[\-]?\d{3}$/
      break
    case 'PT':
      postalCodeRegex = /^\d{4}([\-]\d{3})?$/
      break
    case 'FI':
      postalCodeRegex = /^\d{5}$/
      break
    case 'AX':
      postalCodeRegex = /^22\d{3}$/
      break
    case 'KR':
      postalCodeRegex = /^\d{3}[\-]\d{3}$/
      break
    case 'CN':
      postalCodeRegex = /^\d{6}$/
      break
    case 'TW':
      postalCodeRegex = /^\d{3}(\d{2})?$/
      break
    case 'SG':
      postalCodeRegex = /^\d{6}$/
      break
    case 'DZ':
      postalCodeRegex = /^\d{5}$/
      break
    case 'AD':
      postalCodeRegex = /^AD\d{3}$/
      break
    case 'AR':
      postalCodeRegex = /^([A-HJ-NP-Z])?\d{4}([A-Z]{3})?$/
      break
    case 'AM':
      postalCodeRegex = /^(37)?\d{4}$/
      break
    case 'AZ':
      postalCodeRegex = /^\d{4}$/
      break
    case 'BH':
      postalCodeRegex = /^((1[0-2]|[2-9])\d{2})?$/
      break
    case 'BD':
      postalCodeRegex = /^\d{4}$/
      break
    case 'BB':
      postalCodeRegex = /^(BB\d{5})?$/
      break
    case 'BY':
      postalCodeRegex = /^\d{6}$/
      break
    case 'BM':
      postalCodeRegex = /^[A-Z]{2}[ ]?[A-Z0-9]{2}$/
      break
    case 'BA':
      postalCodeRegex = /^\d{5}$/
      break
    case 'IO':
      postalCodeRegex = /^BBND 1ZZ$/
      break
    case 'BN':
      postalCodeRegex = /^[A-Z]{2}[ ]?\d{4}$/
      break
    case 'BG':
      postalCodeRegex = /^\d{4}$/
      break
    case 'KH':
      postalCodeRegex = /^\d{5}$/
      break
    case 'CV':
      postalCodeRegex = /^\d{4}$/
      break
    case 'CL':
      postalCodeRegex = /^\d{7}$/
      break
    case 'CR':
      postalCodeRegex = /^\d{4,5}|\d{3}-\d{4}$/
      break
    case 'HR':
      postalCodeRegex = /^\d{5}$/
      break
    case 'CY':
      postalCodeRegex = /^\d{4}$/
      break
    case 'CZ':
      postalCodeRegex = /^\d{3}[ ]?\d{2}$/
      break
    case 'DO':
      postalCodeRegex = /^\d{5}$/
      break
    case 'EC':
      postalCodeRegex = /^([A-Z]\d{4}[A-Z]|(?:[A-Z]{2})?\d{6})?$/
      break
    case 'EG':
      postalCodeRegex = /^\d{5}$/
      break
    case 'EE':
      postalCodeRegex = /^\d{5}$/
      break
    case 'FO':
      postalCodeRegex = /^\d{3}$/
      break
    case 'GE':
      postalCodeRegex = /^\d{4}$/
      break
    case 'GR':
      postalCodeRegex = /^\d{3}[ ]?\d{2}$/
      break
    case 'GL':
      postalCodeRegex = /^39\d{2}$/
      break
    case 'GT':
      postalCodeRegex = /^\d{5}$/
      break
    case 'HT':
      postalCodeRegex = /^\d{4}$/
      break
    case 'HN':
      postalCodeRegex = /^(?:\d{5})?$/
      break
    case 'HU':
      postalCodeRegex = /^\d{4}$/
      break
    case 'IS':
      postalCodeRegex = /^\d{3}$/
      break
    case 'IN':
      postalCodeRegex = /^\d{6}$/
      break
    case 'ID':
      postalCodeRegex = /^\d{5}$/
      break
    case 'IL':
      postalCodeRegex = /^\d{5}$/
      break
    case 'JO':
      postalCodeRegex = /^\d{5}$/
      break
    case 'KZ':
      postalCodeRegex = /^\d{6}$/
      break
    case 'KE':
      postalCodeRegex = /^\d{5}$/
      break
    case 'KW':
      postalCodeRegex = /^\d{5}$/
      break
    case 'LA':
      postalCodeRegex = /^\d{5}$/
      break
    case 'LV':
      postalCodeRegex = /^\d{4}$/
      break
    case 'LB':
      postalCodeRegex = /^(\d{4}([ ]?\d{4})?)?$/
      break
    case 'LI':
      postalCodeRegex = /^(948[5-9])|(949[0-7])$/
      break
    case 'LT':
      postalCodeRegex = /^\d{5}$/
      break
    case 'LU':
      postalCodeRegex = /^\d{4}$/
      break
    case 'MK':
      postalCodeRegex = /^\d{4}$/
      break
    case 'MY':
      postalCodeRegex = /^\d{5}$/
      break
    case 'MV':
      postalCodeRegex = /^\d{5}$/
      break
    case 'MT':
      postalCodeRegex = /^[A-Z]{3}[ ]?\d{2,4}$/
      break
    case 'MU':
      postalCodeRegex = /^(\d{3}[A-Z]{2}\d{3})?$/
      break
    case 'MX':
      postalCodeRegex = /^\d{5}$/
      break
    case 'MD':
      postalCodeRegex = /^\d{4}$/
      break
    case 'MC':
      postalCodeRegex = /^980\d{2}$/
      break
    case 'MA':
      postalCodeRegex = /^\d{5}$/
      break
    case 'NP':
      postalCodeRegex = /^\d{5}$/
      break
    case 'NZ':
      postalCodeRegex = /^\d{4}$/
      break
    case 'NI':
      postalCodeRegex = /^((\d{4}-)?\d{3}-\d{3}(-\d{1})?)?$/
      break
    case 'NG':
      postalCodeRegex = /^(\d{6})?$/
      break
    case 'OM':
      postalCodeRegex = /^(PC )?\d{3}$/
      break
    case 'PK':
      postalCodeRegex = /^\d{5}$/
      break
    case 'PY':
      postalCodeRegex = /^\d{4}$/
      break
    case 'PH':
      postalCodeRegex = /^\d{4}$/
      break
    case 'PL':
      postalCodeRegex = /^\d{2}-\d{3}$/
      break
    case 'PR':
      postalCodeRegex = /^00[679]\d{2}([ \-]\d{4})?$/
      break
    case 'RO':
      postalCodeRegex = /^\d{6}$/
      break
    case 'RU':
      postalCodeRegex = /^\d{6}$/
      break
    case 'SM':
      postalCodeRegex = /^4789\d$/
      break
    case 'SA':
      postalCodeRegex = /^\d{5}$/
      break
    case 'SN':
      postalCodeRegex = /^\d{5}$/
      break
    case 'SK':
      postalCodeRegex = /^\d{3}[ ]?\d{2}$/
      break
    case 'SI':
      postalCodeRegex = /^\d{4}$/
      break
    case 'ZA':
      postalCodeRegex = /^\d{4}$/
      break
    case 'LK':
      postalCodeRegex = /^\d{5}$/
      break
    case 'TJ':
      postalCodeRegex = /^\d{6}$/
      break
    case 'TH':
      postalCodeRegex = /^\d{5}$/
      break
    case 'TN':
      postalCodeRegex = /^\d{4}$/
      break
    case 'TR':
      postalCodeRegex = /^\d{5}$/
      break
    case 'TM':
      postalCodeRegex = /^\d{6}$/
      break
    case 'UA':
      postalCodeRegex = /^\d{5}$/
      break
    case 'UY':
      postalCodeRegex = /^\d{5}$/
      break
    case 'UZ':
      postalCodeRegex = /^\d{6}$/
      break
    case 'VA':
      postalCodeRegex = /^00120$/
      break
    case 'VE':
      postalCodeRegex = /^\d{4}$/
      break
    case 'ZM':
      postalCodeRegex = /^\d{5}$/
      break
    case 'AS':
      postalCodeRegex = /^96799$/
      break
    case 'CC':
      postalCodeRegex = /^6799$/
      break
    case 'CK':
      postalCodeRegex = /^\d{4}$/
      break
    case 'RS':
      postalCodeRegex = /^\d{6}$/
      break
    case 'ME':
      postalCodeRegex = /^8\d{4}$/
      break
    case 'CS':
      postalCodeRegex = /^\d{5}$/
      break
    case 'YU':
      postalCodeRegex = /^\d{5}$/
      break
    case 'CX':
      postalCodeRegex = /^6798$/
      break
    case 'ET':
      postalCodeRegex = /^\d{4}$/
      break
    case 'FK':
      postalCodeRegex = /^FIQQ 1ZZ$/
      break
    case 'NF':
      postalCodeRegex = /^2899$/
      break
    case 'FM':
      postalCodeRegex = /^(9694[1-4])([ \-]\d{4})?$/
      break
    case 'GF':
      postalCodeRegex = /^9[78]3\d{2}$/
      break
    case 'GN':
      postalCodeRegex = /^\d{3}$/
      break
    case 'GP':
      postalCodeRegex = /^9[78][01]\d{2}$/
      break
    case 'GS':
      postalCodeRegex = /^SIQQ 1ZZ$/
      break
    case 'GU':
      postalCodeRegex = /^969[123]\d([ \-]\d{4})?$/
      break
    case 'GW':
      postalCodeRegex = /^\d{4}$/
      break
    case 'HM':
      postalCodeRegex = /^\d{4}$/
      break
    case 'IQ':
      postalCodeRegex = /^\d{5}$/
      break
    case 'KG':
      postalCodeRegex = /^\d{6}$/
      break
    case 'LR':
      postalCodeRegex = /^\d{4}$/
      break
    case 'LS':
      postalCodeRegex = /^\d{3}$/
      break
    case 'MG':
      postalCodeRegex = /^\d{3}$/
      break
    case 'MH':
      postalCodeRegex = /^969[67]\d([ \-]\d{4})?$/
      break
    case 'MN':
      postalCodeRegex = /^\d{6}$/
      break
    case 'MP':
      postalCodeRegex = /^9695[012]([ \-]\d{4})?$/
      break
    case 'MQ':
      postalCodeRegex = /^9[78]2\d{2}$/
      break
    case 'NC':
      postalCodeRegex = /^988\d{2}$/
      break
    case 'NE':
      postalCodeRegex = /^\d{4}$/
      break
    case 'VI':
      postalCodeRegex = /^008(([0-4]\d)|(5[01]))([ \-]\d{4})?$/
      break
    case 'PF':
      postalCodeRegex = /^987\d{2}$/
      break
    case 'PG':
      postalCodeRegex = /^\d{3}$/
      break
    case 'PM':
      postalCodeRegex = /^9[78]5\d{2}$/
      break
    case 'PN':
      postalCodeRegex = /^PCRN 1ZZ$/
      break
    case 'PW':
      postalCodeRegex = /^96940$/
      break
    case 'RE':
      postalCodeRegex = /^9[78]4\d{2}$/
      break
    case 'SH':
      postalCodeRegex = /^(ASCN|STHL) 1ZZ$/
      break
    case 'SJ':
      postalCodeRegex = /^\d{4}$/
      break
    case 'SO':
      postalCodeRegex = /^\d{5}$/
      break
    case 'SZ':
      postalCodeRegex = /^[HLMS]\d{3}$/
      break
    case 'TC':
      postalCodeRegex = /^TKCA 1ZZ$/
      break
    case 'WF':
      postalCodeRegex = /^986\d{2}$/
      break
    case 'XK':
      postalCodeRegex = /^\d{5}$/
      break
    case 'YT':
      postalCodeRegex = /^976\d{2}$/
      break
    default:
      postalCodeRegex = /^(?:[A-Z0-9]+([- ]?[A-Z0-9]+)*)?$/
  }
  return postalCodeRegex.test(postalCode)
}

let instance

export const isVideoUrl = (url) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov']
  const extension = url?.split('.')?.pop()?.toLowerCase()
  return videoExtensions?.includes(extension)
}

export const S3Upload = (
  file,
  token,
  identityId,
  userUniqueId,
  folderName,
  shortName,
) => {
  return new Promise((resolve, reject) => {
    AWS.config.update({
      region: process.env.REACT_APP_REGION,
      credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
        IdentityId: identityId,
        Logins: {
          'cognito-identity.amazonaws.com': token,
        },
      }),
    })

    let extension = ''
    if (shortName === 'IMG') {
      extension = 'jpg'
    } else if (shortName === 'VID') {
      extension = 'mp4'
    } else if (shortName === 'AUD') {
      extension = 'mp3'
    } else {
      extension = file?.name?.split('.')[1]
    }

    const S3Client = new AWS.S3()

    const date = new Date()
    const params = {
      Bucket: process.env.REACT_APP_BUCKET_NAME,
      // Using the date.getTime() method for a unique name;
      // however, you may want to consider alternative methods to avoid reliance on this approach.
      Key: `${process.env.REACT_APP_S3_MODE}/${userUniqueId}/${
        folderName === 'profilePic' ? folderName : 'pitch/' + folderName
      }/${shortName}_${date.getTime()}.${extension}`,
      Body: file,
      ContentType: file.type,
    }

    instance = S3Client.upload(params, function (err, data) {
      if (err) {
        instance = undefined
        console.error('Error uploading file:', err)
        reject(err)
      }
      saveFilePath(data?.Location)
      instance = undefined
      resolve({ ...data, name: file.name })
    })
  })
}

export const CompressImage = async (file) => {
  const options = {
    maxSizeMB: 5, // Set to 5MB to stay safely under Textract's 10MB limit
    useWebWorker: true,
    fileType: 'image/jpeg',
    maxWidthOrHeight: 4096, // Increased max dimension while maintaining quality
    initialQuality: 0.7, // Start with slightly lower quality for better compression
  }
  try {
    let compressedFile = await imageCompression(file, options)

    if (compressedFile.size > 5 * 1024 * 1024) {
      options.maxSizeMB = 4.5

      options.initialQuality = 0.5
      compressedFile = await imageCompression(file, options)
    }

    return compressedFile
  } catch (error) {
    console.error('Compression error:', error)
    enqueueSnackbar(
      'Image compression failed, upload a file with smaller size( < 5MB )',
      { variant: 'error' },
    )
    return null
  }
}

const saveFilePath = (path) => {
  try {
    const config = {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
    if (path) {
      axios.post(
        `${process.env.REACT_APP_API_URL}/pitch/file-url/store`,
        { path },
        config,
      )
    }
  } catch (error) {
    console.log(error)
  }
}

export const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'

    video.onloadedmetadata = () => {
      resolve(video.duration)
    }

    video.onerror = (error) => {
      reject(new Error('Error loading video metadata'))
    }

    video.src = URL.createObjectURL(file)
  })

export const generateVideoThumbnail = (file, timeInSeconds = 1) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(timeInSeconds, video.duration || 1)
    }

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailFile = new File([blob], 'thumbnail.jpg', {
                type: 'image/jpeg',
              })
              resolve(thumbnailFile)
            } else {
              reject(new Error('Failed to generate thumbnail blob'))
            }
          },
          'image/jpeg',
          0.8,
        )
      } catch (error) {
        reject(new Error('Error generating thumbnail: ' + error.message))
      } finally {
        URL.revokeObjectURL(video.src)
      }
    }

    video.onerror = (error) => {
      reject(new Error('Error loading video for thumbnail generation'))
      URL.revokeObjectURL(video.src)
    }

    video.src = URL.createObjectURL(file)
  })

export const S3Delete = (path, token, identityId) => {
  return new Promise((resolve, reject) => {
    AWS.config.update({
      region: process.env.REACT_APP_REGION,
      credentials: new AWS.CognitoIdentityCredentials({
        IdentityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
        IdentityId: identityId,
        Logins: {
          'cognito-identity.amazonaws.com': token,
        },
      }),
    })

    const S3Client = new AWS.S3()

    const params = {
      Bucket: process.env.REACT_APP_BUCKET_NAME,
      Key: `${
        process.env.REACT_APP_S3_MODE +
        path?.split(process.env.REACT_APP_S3_MODE)[1]
      }`,
    }

    S3Client.deleteObject(params, function (err, data) {
      if (err) {
        console.error('Error deleting file:', err)
        reject(err)
      }
      resolve()
    })
  })
}

export const S3Abort = () => {
  if (instance) {
    try {
      instance.abort()
    } catch (error) {
      console.log(error)
    }
  }
}

export const encryptMessage = (inputText) => {
  try {
    // Convert key and IV to WordArray
    const key = enc.Utf8.parse(process.env.REACT_APP_AES_ENC_KEY)
    const iv = enc.Utf8.parse(process.env.REACT_APP_AES_ENC_IV)

    // Encrypt the input text
    const encrypted = AES.encrypt(inputText, key, {
      iv: iv,
      mode: mode.CBC,
      padding: pad.Pkcs7,
    })

    return encrypted.toString()
  } catch (e) {
    console.error(e)
    return ''
  }
}

export const decryptMessage = (inputText) => {
  try {
    if (inputText) {
      // Convert key and IV to WordArray
      const key = enc.Utf8.parse(process.env.REACT_APP_AES_ENC_KEY)
      const iv = enc.Utf8.parse(process.env.REACT_APP_AES_ENC_IV)

      // Decrypt the input text
      const encrypted = AES.decrypt(inputText, key, {
        iv: iv,
        mode: mode.CBC,
        padding: pad.Pkcs7,
      })

      return encrypted.toString(enc.Utf8)
    } else {
      return ''
    }
  } catch (e) {
    console.error(e)
    return ''
  }
}

export const convertToHttpUrl = (url) => {
  // Check if the URL starts with 'www.'
  if (url.startsWith('http')) {
    // Prepend 'http://' to the URL
    return url
  } else {
    return `http://${url}`
  }
}

export const getDateDiff = (startDate, endDate) => {
  return moment
    .unix(startDate)
    .startOf('day')
    .diff(moment.unix(endDate).startOf('day'), 'days')
}

// eslint-disable-next-line no-unused-vars
const isYesterday = (date) => {
  if (!date) return false // If the date is null or undefined, return false

  const yesterday = moment().subtract(1, 'days').startOf('day') // Get yesterday's date and set to start of the day
  const inputDate = moment(date).startOf('day') // Convert the input date to Moment.js object and set to start of the day

  return yesterday.isSame(inputDate) // Compare if both dates are the same
}

export const getTimeAgo = (date) => {
  let seconds = moment().diff(moment.unix(date), 'seconds')
  let minutes = Math.floor(seconds / 60)
  let hours = Math.floor(minutes / 60)
  let days = Math.floor(hours / 24)
  let phrase = ''

  if (seconds <= 0) {
    phrase = 'few moments ago'
  } else if (seconds < 45) {
    phrase = `${seconds} sec ago`
  } else if (seconds < 90) {
    phrase = '1 min ago'
  } else if (minutes < 45) {
    phrase = `${minutes} min ago`
  } else if (minutes < 90) {
    phrase = '1h ago'
  } else if (hours < 24) {
    phrase = `${hours}h ago`
  } else if (hours < 42) {
    phrase = 'Yesterday'
  } else if (days < 30) {
    phrase = `${days}d ago`
  } else if (days < 45) {
    phrase = '1 m ago'
  } else if (days < 365) {
    phrase = `${Math.floor(days / 30)} m ago`
  } else {
    phrase = moment.unix(date).format('MM/DD/YY')
  }

  return phrase
}

export const isValidYouTubeUrl = (url) => {
  if (!url || url.trim() === '') return false

  const regex = new RegExp(
    '^(https?:\\/\\/)?(www\\.)?' +
      '(' +
      'youtube\\.com\\/(watch\\?v=|embed\\/|v\\/|shorts\\/)|' + // videos
      'youtube\\.com\\/playlist\\?list=|' + // playlists
      'youtu\\.be\\/' + // short links
      ')' +
      '([A-Za-z0-9-]{11}|[A-Za-z0-9-]{10,})' + // videoId OR playlistId
      '(\\S+)?$',
    'i',
  )

  return regex.test(url.trim())
}
